import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser, setUser } from "../redux/userSlice";
import { FiLogOut, FiUser, FiUsers } from "react-icons/fi";
import { MedalIcon } from "../components/tickets/Tickets";
import { Dropdown } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import UserAvatarFrame from "./UserAvatarFrame";
import { resetFilter } from "../redux/filterReducer";
import { IoSearchSharp } from "react-icons/io5";
import * as general from "../libs/general";
import { useTranslation } from "react-i18next";
import HeaderMain from "./HeaderMain";
import { TiSocialFacebook } from "react-icons/ti";
import { LuMail } from "react-icons/lu";
import { IoCallOutline } from "react-icons/io5";
import { IoMenuSharp } from "react-icons/io5";
import axios from "axios";
import { API_URL_All } from "../api";

import { canViewChildFeature, CHILD_FEATURES } from "../libs/childUserPermission";
import "../css/header.css";

const Header = () => {
  const config = general.getLocalStorage("user_config");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isNewsMenuOpen, setIsNewsMenuOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // Đã xử lý khi token hết hạn thì tự động logout trong axiosInstance.js
  //useEffect(() => {
  //  const logoutTimer = setTimeout(() => {
  //    dispatch(logoutUser());
  //    dispatch(resetFilter());
  //    sessionStorage.removeItem('itemsPerPage');
  //    sessionStorage.removeItem('currentPage');
  //    sessionStorage.removeItem('filters');
  //    navigate("/dang-nhap");
  //  }, 120 * 60 * 1000); // 120 phút

  //  return () => clearTimeout(logoutTimer);
  //}, [dispatch, navigate]);

  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const userDisplayName =
    (config?.name || user?.name || user?.email || "").trim() || "Người dùng";
  const tradeVisitorId = String(
    config?.userId ?? user?.id ?? user?.userId ?? ""
  ).trim();
  const tradeVisitorNameBase = (
    config?.name ||
    user?.name ||
    user?.username ||
    user?.email ||
    userDisplayName ||
    ""
  ).trim();
  const tradeCustomerCode = String(
    config?.code || user?.customerCode || ""
  ).trim();
  /** trade_web_client.js → Double Boss: "DL03: Nguyễn Ánh" / "ZTO-DL03: …" */
  const tradeVisitorName = tradeCustomerCode
    ? `${tradeCustomerCode}: ${tradeVisitorNameBase}`
    : tradeVisitorNameBase;
  const canManageChildUsers = config?.canManageChildUsers === true;
  const canViewOrders = canViewChildFeature(config, CHILD_FEATURES.ORDERS);
  const canViewTth = canViewChildFeature(config, CHILD_FEATURES.TTH_VIEW);
  const canViewProducts = canViewChildFeature(config, CHILD_FEATURES.PRODUCTS);
  const canViewReconcileNormal = canViewChildFeature(config, CHILD_FEATURES.RECONCILE_NORMAL);
  const canViewReconcileVat = canViewChildFeature(config, CHILD_FEATURES.RECONCILE_VAT);
  const canViewReconciliation =
    canViewReconcileNormal || canViewReconcileVat;
  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const handleLogin = () => navigate("/dang-nhap");
  const handleLogout = () => {
    dispatch(resetFilter());
    sessionStorage.removeItem("itemsPerPage");
    sessionStorage.removeItem("currentPage");
    sessionStorage.removeItem("filters");
    dispatch(logoutUser());
    navigate("/dang-nhap");
  };

  // Điều hướng đến trang thông tin cá nhân
  const handleViewProfile = () => {
    navigate(`/profile`);
  };

  const handleViewChildUsers = () => {
    navigate("/danh-sach-users");
  };

  const handleNavigationClick = () => {
    setIsMenuOpen(false);
    setIsNewsMenuOpen(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError("Vui lòng nhập mã vận đơn!");
      return;
    }

    setSearchLoading(true);
    setSearchError("");

    try {
      // API public: gọi không kèm token để khách chưa đăng nhập vẫn tra cứu được.
      const response = await axios.get(`${API_URL_All}/api/clients/findOrderLogs`, {
        params: { refer: searchQuery.trim() },
      });
      const obj = response.data;

      if (obj.success === false) {
        setSearchError(obj.message);
        navigate(`/tra-cuu-don-hang`, { state: { message: obj.message, searchRefer: searchQuery } });
      } else if (obj.data && obj.data.order && obj.data.order.id) {
        // Điều hướng đến trang tra cứu với kết quả
        navigate(`/tra-cuu-don-hang`, { state: { orderData: obj.data, searchRefer: searchQuery } });
        setSearchQuery("");
      } else {
        setSearchError("Không tìm thấy đơn hàng");
      }
    } catch (error) {
      console.error("Error fetching order status:", error);
      setSearchError("Lỗi khi tìm kiếm trạng thái");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const handleNewsMenuEnter = () => {
    setIsNewsMenuOpen(true);
  };

  const handleNewsMenuLeave = () => {
    setIsNewsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 200; // px scrolled before fixing header-nav
      setIsScrolled(window.scrollY > threshold);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("persist:root");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const user =
        parsedUser.user && parsedUser.user !== "null"
          ? JSON.parse(parsedUser.user)
          : null;
      if (user) {
        dispatch(setUser(user));
      }
    }
  }, [dispatch]);

  return (
    <header
      className={`container-fluid navbar navbar-expand-lg user-menu header bg-white d-flex flex-column ${
        isScrolled ? "scrolled" : ""
      }`}
    >
      {/* <div className="w-100 bg-zto p-1 header-top d-none d-md-block">
        <div className="container">
          <div className="d-flex">
            <div className="col-sm-6 d-flex flex-start align-items-center">
              <a href="https://facebook.com/ztovietnam">
                <TiSocialFacebook size={20} />
              </a>
              <a href="mailto:ztovietnam.vn@gmail.com ">
                <LuMail size={20} />
              </a>
            </div>
            <div className="col-sm-6 d-flex flex-end">
              <a href="/">
                <LuMail size={20} /> {t("header.contact")}
              </a>
              <span style={{ margin: "0 0.5rem" }}>|</span>
              <a href="tel:0364758606">
                <IoCallOutline size={20} />
                <span>0364 758 606</span>
              </a>
            </div>
          </div>
        </div>
      </div> */}
      {!isLoggedIn && <div className="container">
        <HeaderMain />
      </div>}

      <div className="bg-zto w-100 header-nav">
        <div className="container d-flex justify-content-between align-items-center header-nav-inner">
          <div className="col-sm-9 d-flex flex-start header-nav-left">
            <button
              type="button"
              className="navbar-toggler d-lg-none"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
              aria-expanded={isMenuOpen}
              aria-controls="navbarNav"
            >
              <IoMenuSharp size={24} color="#ffffff" />
            </button>
            <div
              className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`}
              id="navbarNav"
            >
              <ul className="navbar-nav">
                {/* Menu "Đơn hàng" phân cấp — đồng bộ với AppHeader.jsx và menu
                    ĐƠN HÀNG của trang admin: 1 mục cha, các loại đơn là mục con. */}
                {isLoggedIn && (canViewOrders || canViewTth) && (
                  <li className="nav-item dropdown">
                    <button
                      type="button"
                      className="nav-link dropdown-toggle"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {t("header.orders")}
                    </button>
                    <ul className="dropdown-menu">
                      {canViewOrders && (
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/order"
                            onClick={handleNavigationClick}
                          >
                            {t("header.ordersNormal")}
                          </Link>
                        </li>
                      )}
                      {canViewTth && (
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/don-tth"
                            onClick={handleNavigationClick}
                          >
                            {t("header.tth")}
                          </Link>
                        </li>
                      )}
                    </ul>
                  </li>
                )}

                {isLoggedIn && canViewProducts && (
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="/user-products"
                      onClick={handleNavigationClick}
                    >
                      {t("header.products")}
                    </Link>
                  </li>
                )}

                {!isLoggedIn && (
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="/"
                      onClick={handleNavigationClick}
                    >
                      {t("header.home")}
                    </Link>
                  </li>
                )}

                {!isLoggedIn && (
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="/about-us"
                      onClick={handleNavigationClick}
                    >
                      {t("header.about")}
                    </Link>
                  </li>
                )}

                {!isLoggedIn && (
                  <div
                    className="dropdown"
                    onMouseEnter={handleNewsMenuEnter}
                    onMouseLeave={handleNewsMenuLeave}
                  >
                    <li className="nav-item nav-item-news">
                      <Link
                        className="nav-link"
                        to="/news"
                        onClick={handleNavigationClick}
                      >
                        {t("header.news")}
                      </Link>
                    </li>

                    <ul
                      className={`dropdown-menu news ${
                        isNewsMenuOpen ? "show" : ""
                      }`}
                    >
                      <li className="nav-item">
                        <Link
                          className="nav-link"
                          to="/market-news"
                          onClick={handleNavigationClick}
                        >
                          {t("header.newsMarket")}
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className="nav-link"
                          to="/knowledge-news"
                          onClick={handleNavigationClick}
                        >
                          {t("header.industryKnowledge")}
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className="nav-link"
                          to="/source-news"
                          onClick={handleNavigationClick}
                        >
                          {t("header.source")}
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}

                {!isLoggedIn && (
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="/instruction"
                      onClick={handleNavigationClick}
                    >
                      {t("header.guide")}
                    </Link>
                  </li>
                )}

                {isLoggedIn && canViewReconciliation && (
                  <li className="nav-item dropdown">
                    <button
                      type="button"
                      className="nav-link dropdown-toggle"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {t("header.reconciliation")}
                    </button>
                    {config.lineIds &&
                      (config.lineIds.includes(1) ||
                        config.lineIds.includes(4) ||
                        config.lineIds.includes(11) ||
                        config.lineIds.includes(12)) && (
                        <ul className="dropdown-menu">
                          {canViewReconcileNormal &&
                            config.lineIds &&
                            (config.lineIds.includes(1) ||
                              config.lineIds.includes(4) ||
                              config.lineIds.includes(11)) && (
                              <li>
                                <Link className="dropdown-item" to="/doi-soat">
                                  {t("header.lineNormal")}
                                </Link>
                              </li>
                            )}
                          {canViewReconcileVat &&
                            config.lineIds &&
                            config.lineIds.includes(12) && (
                            <li>
                              <Link
                                className="dropdown-item"
                                to="/doi-soat-vat"
                              >
                                {t("header.lineVAT")}
                              </Link>
                            </li>
                          )}
                          <li className="d-none"></li>
                        </ul>
                      )}
                    <ul className="dropdown-menu d-none"></ul>
                  </li>
                )}

                <li className="nav-item">
                  <Link
                    className="nav-link"
                    to="/chinh-sach"
                    onClick={handleNavigationClick}
                  >
                    {t("header.policy")}
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className="nav-link text-white"
                    to="/lien-he"
                    onClick={handleNavigationClick}
                  >
                    {t("header.contact")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {isLoggedIn === true && (
            <div className="col-sm-3 d-flex align-items-center user-section flex-end">
              <MedalIcon rank={config.userRank} />
              {user?.isAdmin !== true &&
                user?.admin !== true &&
                user?.isMember !== true &&
                user?.member !== true && (
                  <span
                    className="trade-web-visitor-identity"
                    hidden
                    aria-hidden="true"
                    data-trade-visitor-id={tradeVisitorId}
                    data-trade-visitor-name={tradeVisitorName}
                  />
                )}
              <Dropdown>
                <Dropdown.Toggle variant="" id="dropdown-user">
                  <UserAvatarFrame user={user} config={config} imgAlt="User Avatar" />
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" className="user-avatar-dropdown">
                  <div className="px-3 py-2">
                    <div className="user-avatar-dropdown__name fw-semibold text-body">
                      {userDisplayName}
                    </div>
                  </div>
                  <Dropdown.Divider className="my-0" />
                  {canManageChildUsers && (
                    <Dropdown.Item onClick={handleViewChildUsers} className="dropdown-item">
                      <FiUsers size={18} className="me-2" /> Danh sách users
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item
                    onClick={handleViewProfile}
                    className="dropdown-item"
                  >
                    <FiUser size={18} className="me-2" /> Thông tin cá nhân
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={handleLogout}
                    className="text-danger dropdown-item"
                  >
                    <FiLogOut size={18} className="me-2" /> Đăng xuất
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
          {isLoggedIn === false && (
            <div className="col-sm-3 d-flex align-items-center w-85 justify-content-between header-actions">
              <div className="input-group search-box">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder={t("header.searchPlaceholder")}
                  aria-label="Tra cứu mã vận đơn"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <button
                  className="btn search-btn"
                  type="button"
                  onClick={handleSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? "..." : <IoSearchSharp size={20} />}
                </button>
              </div>
              <button className="btn p-0 m-0 btn-login" onClick={handleLogin}>
                <FaUserCircle
                  size={35}
                  className="login-icon"
                  style={{ color: "white" }}
                />
              </button>

              {/* <div className="d-flex align-items-center gap-2 translate-btn">
                <div className="btn-group" role="group" aria-label="Language">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      i18n.language === "vi" ? "btn-light" : "btn-outline-light"
                    }`}
                    onClick={() => handleLanguageChange("vi")}
                  >
                    VI
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      i18n.language === "en" ? "btn-light" : "btn-outline-light"
                    }`}
                    onClick={() => handleLanguageChange("en")}
                  >
                    EN
                  </button>
                </div>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
